import * as THREE from "three";
import type { DestructionEvent } from "./shared/types";

export class DestructionEffects {
  private effects: { root: THREE.Group; body?: THREE.Object3D; started: number; building: boolean; materials: THREE.Material[]; geometry: THREE.BufferGeometry[]; chunks: { mesh: THREE.Mesh; source: Float32Array; start: number; count: number; center: THREE.Vector3; seed: number; floor: number }[]; dust?: THREE.InstancedMesh; settled?: boolean }[] = [];
  private seen = new Set<string>();
  constructor(private scene: THREE.Scene) {}

  add(event: DestructionEvent, source?: THREE.Object3D): void {
    if (this.seen.has(event.entityId)) return;
    this.seen.add(event.entityId);
    if (this.seen.size > 512) this.seen.delete(this.seen.values().next().value!);
    const root = new THREE.Group();
    root.position.set(event.x, .025, event.z);
    const materials: THREE.Material[] = [], geometry: THREE.BufferGeometry[] = [];
    const chunks: typeof this.effects[number]["chunks"] = [];
    const body = source?.getObjectByName(event.kind === "building" ? "building-visual" : "actor-visual")?.clone(true);
    if (body) {
      const fire = body.getObjectByName("building-fire");
      fire?.removeFromParent();
      body.rotation.y = source!.rotation.y;
      body.traverse(child => {
        child.userData = {};
        if (child instanceof THREE.Mesh) {
          if (event.kind === "building") {
            child.geometry = child.geometry.clone();
            geometry.push(child.geometry);
            const position = child.geometry.getAttribute("position");
            const source = new Float32Array(position.array);
            let start = 0;
            for (const count of child.geometry.userData.parts ?? [position.count]) {
              const center = new THREE.Vector3();
              for (let i = start; i < start + count; i++) center.add(new THREE.Vector3().fromBufferAttribute(position, i));
              center.divideScalar(count);
              const floor = child.worldToLocal(body.localToWorld(new THREE.Vector3(0, .06, 0))).y;
              chunks.push({ mesh: child, source, start, count, center, seed: chunks.length * 2.399, floor });
              start += count;
            }
          }
          const copies = (Array.isArray(child.material) ? child.material : [child.material]).map(m => {
            const copy = m.clone(); copy.transparent = true; materials.push(copy); return copy;
          });
          child.material = Array.isArray(child.material) ? copies : copies[0];
        }
      });
      root.add(body);
    }
    const debris = event.kind === "building" || ["onager", "bombard_cannon"].includes(event.entityType);
    for (let i = 0; i < (debris ? 14 : 5); i++) {
      const g = debris ? new THREE.BoxGeometry(.12, .1, .45 + Math.random() * .5) : new THREE.CircleGeometry(.16 + Math.random() * .25, 9);
      const m = new THREE.MeshBasicMaterial({ color: debris ? (i % 3 ? 0x705237 : 0x77736a) : 0x681e20, transparent: true, depthWrite: false });
      geometry.push(g); materials.push(m);
      const mesh = new THREE.Mesh(g, m);
      const angle = i * 2.4, radius = Math.random() * (debris ? 2.4 : .6);
      mesh.position.set(Math.cos(angle) * radius, .01 + i * .001, Math.sin(angle) * radius);
      mesh.rotation.set(debris ? .2 : -Math.PI / 2, debris ? angle : 0, angle);
      root.add(mesh);
    }
    let dust: THREE.InstancedMesh | undefined;
    if (event.kind === "building") {
      const g = new THREE.IcosahedronGeometry(1, 1);
      const m = new THREE.MeshBasicMaterial({ color: 0x9b8c70, transparent: true, opacity: .3, depthWrite: false });
      geometry.push(g); // Dust has its own short fade, separate from rubble.
      dust = new THREE.InstancedMesh(g, m, 10);
      dust.frustumCulled = false;
      root.add(dust);
    }
    this.scene.add(root);
    this.effects.push({ root, body, started: performance.now(), building: debris, materials, geometry, chunks, dust });
    if (this.effects.length > 80) this.remove(0);
  }

  update(now: number): void {
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i], age = Math.max(0, (now - effect.started) / 1000);
      if (age >= 8) { this.remove(i); continue; }
      if (effect.body) {
        const fall = Math.min(1, age / .7);
        if (effect.chunks.length) {
          // Staggered rigid-piece falls replace the old whole-building squash.
          if (!effect.settled) for (const chunk of effect.chunks) {
            const t = Math.max(0, Math.min(1, (age - (Math.sin(chunk.seed) + 1) * .12) / 1.25));
            const { center, source, start, count, mesh, seed, floor } = chunk;
            const angle = Math.sin(seed) * t * 1.5;
            const position = mesh.geometry.getAttribute("position");
            for (let j = start; j < start + count; j++) {
              const x = source[j * 3] - center.x, y = source[j * 3 + 1] - center.y;
              position.setXYZ(j,
                center.x + x * Math.cos(angle) - y * Math.sin(angle) + Math.cos(seed) * t * .7,
                Math.max(floor, floor + (center.y - floor) * (1 - t * t) + (x * Math.sin(angle) + y * Math.cos(angle)) * (1 - t * .75) + t * (.12 + Math.abs(Math.sin(seed)) * .25)),
                source[j * 3 + 2] + Math.sin(seed) * t * .7);
            }
            position.needsUpdate = true;

          }
          if (age >= 1.5 && !effect.settled) {
            for (const mesh of new Set(effect.chunks.map(chunk => chunk.mesh))) {
              mesh.geometry.computeVertexNormals(); mesh.geometry.computeBoundingSphere();
            }
            effect.settled = true;
          }
        } else if (effect.building) {
          effect.body.scale.y = 1 - fall * .92;
          effect.body.rotation.z = fall * .12;
        } else effect.body.rotation.z = fall * Math.PI / 2;
      }
      if (effect.dust) {
        const transform = new THREE.Object3D();
        effect.dust.visible = age < 2.8;
        (effect.dust.material as THREE.MeshBasicMaterial).opacity = Math.max(0, .32 * (1 - age / 2.8));
        for (let j = 0; j < 10 && age < 2.8; j++) {
          const angle = j * 2.399, radius = .4 + age * 1.2;
          transform.position.set(Math.cos(angle) * radius, .2 + age * .35, Math.sin(angle) * radius);
          transform.scale.setScalar(.2 + Math.min(age, 1.5) * .65);
          transform.updateMatrix(); effect.dust.setMatrixAt(j, transform.matrix);
        }
        effect.dust.instanceMatrix.needsUpdate = true;
      }
      for (const material of effect.materials) material.opacity = Math.max(0, Math.min(1, (8 - age) / 2));
    }
  }

  private remove(index: number): void {
    const [effect] = this.effects.splice(index, 1);
    this.scene.remove(effect.root);
    effect.materials.forEach(m => m.dispose());
    effect.geometry.forEach(g => g.dispose());
    if (effect.dust) { (effect.dust.material as THREE.Material).dispose(); effect.dust.dispose(); }
  }
}
